import {assert} from 'Asserts';
import * as LiveCellValues from 'notebook/structured-tables/LiveCellValues';
import {StructuredTable, StructuredTableAdapter} from 'notebook/structured-tables/StructuredTableConstants';
import getInitialCellValueFromLiveValue, {
  InitialCellValue,
} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import {InsertableColumnInfo} from 'notebook/tables/TableConstants';

/**
 * Returns an array of InitialCellValues for the given StructuredTable and
 * InsertableColumnInfo. This array should have as many items as the
 * StructuredTable has rows. The results of this function can be used as the
 * initial values argument for any of the 'insertColumns*' TableOps.
 *
 * @param adapter - the StructuredTableAdapter that matches the given StructuredTable
 * @param structuredTable - the StructuredTable we're attempting to add a column onto
 * @param columnInfo - the InsertableColumnInfo that we want values for
 */
export default function getInitialCellValuesForColumn<TTableMetadata, TRowMetadata>(
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>,
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
  columnInfo: Partial<InsertableColumnInfo>
): Array<InitialCellValue> {
  const newTableColumn = columnInfo.columnConstraints?.structuredTableColumn ?? null;
  assert(newTableColumn != null, 'Attempting to get values for a null column');
  const storeData = adapter.getStoreDataForLiveValues(structuredTable);
  const primarySamplesByRowIds = adapter.getPrimarySamples(structuredTable);
  const primarySamplesByRow = structuredTable.rows.map((row) => primarySamplesByRowIds[row.rowId]);
  const liveCellValuesForNewTableColumns = LiveCellValues.gatherLiveCellValuesForTable({
    tableSchemas: structuredTable.schemas,
    structuredTableColumns: [newTableColumn],
    adapter,
    primarySamplesByRow,
    storeData,
  });

  return liveCellValuesForNewTableColumns.flatMap((rowOfLiveCellValues) => {
    assert(rowOfLiveCellValues.length <= 1, 'Expected only 1 new column');
    return getInitialCellValueFromLiveValue(
      rowOfLiveCellValues.length === 0 ? 'NO_VALUE' : rowOfLiveCellValues[0]
    );
  });
}
